import { useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY = 'mercury_idle_last_activity';
const THROTTLE_MS = 1000;
const CHECK_INTERVAL_MS = 1000;

export type UseIdleTimeoutOptions = {
  timeout: number;
  warningTime: number;
  onWarning: () => void;
  onIdle: () => void;
  onActive: () => void;
  enabled?: boolean;
};

export function useIdleTimeout({
  timeout,
  warningTime,
  onWarning,
  onIdle,
  onActive,
  enabled = true,
}: UseIdleTimeoutOptions) {
  const lastActivityRef = useRef<number>(Date.now());
  const isWarningRef = useRef<boolean>(false);
  const throttleRef = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    try {
      localStorage.setItem(STORAGE_KEY, now.toString());
    } catch {
      // localStorage may be unavailable
    }

    if (isWarningRef.current) {
      isWarningRef.current = false;
      onActive();
    }
  }, [onActive]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - throttleRef.current < THROTTLE_MS) {
      return;
    }
    throttleRef.current = now;
    updateActivity();
  }, [updateActivity]);

  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || !event.newValue) {
        return;
      }

      const remoteTimestamp = parseInt(event.newValue, 10);
      if (!isNaN(remoteTimestamp) && remoteTimestamp > lastActivityRef.current) {
        lastActivityRef.current = remoteTimestamp;

        if (isWarningRef.current) {
          isWarningRef.current = false;
          onActive();
        }
      }
    },
    [onActive]
  );

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      try {
        const storedTimestamp = localStorage.getItem(STORAGE_KEY);
        if (storedTimestamp) {
          const parsed = parseInt(storedTimestamp, 10);
          if (!isNaN(parsed) && parsed > lastActivityRef.current) {
            lastActivityRef.current = parsed;
            if (isWarningRef.current) {
              isWarningRef.current = false;
              onActive();
            }
          }
        }
      } catch {
        // localStorage may be unavailable
      }
    }
  }, [onActive]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const events: (keyof DocumentEventMap)[] = [
      'mousemove',
      'keydown',
      'click',
      'scroll',
      'touchstart',
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    updateActivity();

    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      const timeUntilIdle = timeout - elapsed;

      if (elapsed >= timeout) {
        onIdle();
        return;
      }

      if (timeUntilIdle <= warningTime && !isWarningRef.current) {
        isWarningRef.current = true;
        onWarning();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    enabled,
    timeout,
    warningTime,
    handleActivity,
    handleStorageChange,
    handleVisibilityChange,
    updateActivity,
    onWarning,
    onIdle,
  ]);

  return {
    resetTimer: updateActivity,
  };
}
